import * as THREE from 'three';
import BaseModel3d from '../BaseModel3d';
import { globalStores } from '@/editor/stores';
import { pixelRatio3d } from '@/editor/math/const';
import { IBaseDataCylinderFace, IFaceData } from '@/editor/data/face/face';
import { EEntityType } from '@/editor/types';
import { EContainerType } from '@/editor/types/entity';
import { createMaterial } from '@/editor/data/material/utils';
import { Shape, ShapeGeometry, Vector2, Vector3 } from 'three';
import { getCircleContour } from '@/editor/utils/face';
import { disposeAllChildren, disposeAllChildrenThenRemoveFromParent } from '../../utils';
import { EFaceType } from '../../type';
import { COLUMN_RENDER_OFFSET } from '@/editor/constant/column';
import { calcNormal } from './utils';

export class CylinderFaceModel extends BaseModel3d {
  private _planeMesh?: THREE.Mesh;
  private _topBottomPlaneGroup: THREE.Group = new THREE.Group();

  constructor(options: { id: string }) {
    super({ id: options.id });

    this.model.userData.type = EEntityType.Face;
    this.model.userData.containerType = EContainerType.entity;
    this.model.userData.entityId = this._id;

    globalStores.editorStateStore.sceneInfo.scene3d.intersectableGroup.add(this.model);
    globalStores.editorStateStore.sceneInfo.scene3d.scene.add(this._topBottomPlaneGroup);
  }

  createModel(faceData: IFaceData) {
    if (!this.canUpdate) return;

    this.clearModel();

    // 添加到容器
    this._planeMesh = this.createPlane(faceData);
    this.model.add(this._planeMesh);

    this._topBottomPlaneGroup.add(...this.createTopBottomPlane(faceData));
  }

  private createPlane(faceData: IFaceData) {
    const { radius, center, height, offGround } = faceData.baseData as IBaseDataCylinderFace;

    const shrinkRadius = radius - COLUMN_RENDER_OFFSET;
    const rectFaceCount = 64;
    const arcLength = (shrinkRadius * Math.PI * 2) / rectFaceCount;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(rectFaceCount * 6 * 3);
    const uvs = new Float32Array(rectFaceCount * 6 * 2);
    const normals = new Float32Array(rectFaceCount * 6 * 3);
    const contour = getCircleContour(center, shrinkRadius, rectFaceCount).reverse();

    for (let i = 0; i < contour.length; i++) {
      const start = contour[i];
      const end = contour[(i + 1) % contour.length];
      const startBottom = new Vector3(start.x, start.y, offGround);
      const endBottom = new Vector3(end.x, end.y, offGround);
      const startTop = new Vector3(start.x, start.y, offGround + height);
      const endTop = new Vector3(end.x, end.y, offGround + height);

      const startBottomUV = new Vector2(arcLength * i, offGround);
      const endBottomUV = new Vector2(arcLength * (i + 1), offGround);
      const startTopUV = new Vector2(arcLength * i, offGround + height);
      const endTopUV = new Vector2(arcLength * (i + 1), offGround + height);

      const triangle1 = [
        endBottom.x,
        endBottom.y,
        endBottom.z,
        startBottom.x,
        startBottom.y,
        startBottom.z,
        startTop.x,
        startTop.y,
        startTop.z
      ];
      const uv1 = [endBottomUV.x, endBottomUV.y, startBottomUV.x, startBottomUV.y, startTopUV.x, startTopUV.y];
      const triangle2 = [
        startTop.x,
        startTop.y,
        startTop.z,
        endTop.x,
        endTop.y,
        endTop.z,
        endBottom.x,
        endBottom.y,
        endBottom.z
      ];
      const uv2 = [startTopUV.x, startTopUV.y, endTopUV.x, endTopUV.y, endBottomUV.x, endBottomUV.y];

      const normal = calcNormal(startBottom, startTop, endTop);
      for (let j = 0; j < 6; j++) {
        normals[i * 6 * 3 + j * 3] = normal.x;
        normals[i * 6 * 3 + j * 3 + 1] = normal.y;
        normals[i * 6 * 3 + j * 3 + 2] = normal.z;
      }

      triangle1.concat(triangle2).forEach((value, index) => {
        positions[i * 6 * 3 + index] = value / pixelRatio3d;
      });
      uv1.concat(uv2).forEach((value, index) => {
        uvs[i * 6 * 2 + index] = value / pixelRatio3d;
      });
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));

    const material = createMaterial(faceData.materialInfo.id);
    const plane = new THREE.Mesh(geometry, material);
    return plane;
  }

  private createTopBottomPlane(faceData: IFaceData) {
    const { radius, center, height, offGround } = faceData.baseData as IBaseDataCylinderFace;

    const rectFaceCount = 64;
    const contour = getCircleContour(center, radius, rectFaceCount).reverse();

    const shape = new Shape();
    contour.forEach((point, index) => {
      if (index === 0) {
        shape.moveTo(point.x / pixelRatio3d, point.y / pixelRatio3d);
      } else {
        shape.lineTo(point.x / pixelRatio3d, point.y / pixelRatio3d);
      }
    });
    const geometry = new ShapeGeometry(shape);
    const materialId = globalStores.getDefaultMaterial({ type: EFaceType.ColumnFace }).id;
    const bottomMaterial = createMaterial(materialId, false, THREE.BackSide);
    const bottomPlane = new THREE.Mesh(geometry, bottomMaterial);
    bottomPlane.position.set(0, 0, offGround / pixelRatio3d);

    const topMaterial = createMaterial(materialId);
    const topPlane = new THREE.Mesh(geometry, topMaterial);
    topPlane.position.set(0, 0, (height + offGround) / pixelRatio3d);

    return [bottomPlane, topPlane];
  }

  updateWithoutDestroy(faceData: IFaceData) {
    if (!this.canUpdate) return;

    if (this._planeMesh) {
      this._planeMesh.material = createMaterial(faceData.materialInfo.id);
    }
  }

  override clearModel() {
    disposeAllChildren(this._topBottomPlaneGroup);
    super.clearModel();
  }

  override setVisible(visible: boolean) {
    this.model.visible = visible;
    this._topBottomPlaneGroup.visible = visible;
  }

  override dispose() {
    disposeAllChildrenThenRemoveFromParent(this._topBottomPlaneGroup);
    super.dispose();
  }
}
